import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Progress,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import {
  AwardMethodDto,
  PublicBidStatus,
  SourcingEventStatus,
  type EvalBidDto,
  type EvalCriterionDto,
  type SourcingEventV2Dto,
  useBeginEvaluationMutation,
  useGetEvaluationQuery,
  useSetCriterionRationaleMutation,
  useUpsertCriterionScoreMutation,
  useUpsertPriceOverrideMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { isEventAwarded } from "../bidOutcome";
import { ScoreCell } from "./ScoreCell";
import { PriceCell } from "./PriceCell";
import { CriterionRationaleSection } from "./CriterionRationaleSection";
import { EvaluationRanking } from "./EvaluationRanking";

const bidDeadlinePassed = (event: SourcingEventV2Dto) => {
  const deadline = event.eventData.bidSubmissionDeadline;
  return deadline ? new Date(deadline).getTime() <= Date.now() : false;
};

export const EvaluationTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const eventId = event.id;
  const readOnly = event.status !== SourcingEventStatus.Evaluation;
  const awarded = isEventAwarded(event.status);
  const { data, isLoading, isError } = useGetEvaluationQuery({ eventId });
  const [upsertScore] = useUpsertCriterionScoreMutation();
  const [setRationale] = useSetCriterionRationaleMutation();
  const [upsertPriceOverride] = useUpsertPriceOverrideMutation();

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !data) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (data.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">
          {bidDeadlinePassed(event)
            ? t("No bids delivered - nothing to evaluate")
            : t("Evaluation can start once the bid submission deadline has passed")}
        </Text>
      </Center>
    );
  }

  const cellFor = (bid: EvalBidDto, criterion: EvalCriterionDto) =>
    bid.cells.find((c) => c.criterionId === criterion.id);

  const progressPct =
    data.progress.total > 0
      ? (data.progress.filled / data.progress.total) * 100
      : 0;

  return (
    <Stack spacing="8">
      {event.status === SourcingEventStatus.Published && (
        <BeginEvaluationPanel event={event} />
      )}
      <Box>
        <Heading size="md" mb="1">
          {t("Evaluation")}
        </Heading>
        <Flex align="center" gap="1">
          <Text fontSize="sm" color="smMuted">
            {data.awardMethod === AwardMethodDto.LowestPrice
              ? t("Lowest price")
              : t("Best price-quality ratio")}
          </Text>
          <PriceScoringInfo />
        </Flex>
        {readOnly && (
          <Text fontSize="sm" color="smMuted" mt="1">
            {t(
              "Scoring and assessments are available only while the procurement is being evaluated.",
            )}
          </Text>
        )}
      </Box>

      <Flex align="center" gap="3">
        <Progress
          value={progressPct}
          size="sm"
          rounded="full"
          colorScheme={
            data.progress.filled === data.progress.total ? "green" : "teal"
          }
          flex="1"
        />
        <Text
          fontSize="sm"
          color="smMuted"
          whiteSpace="nowrap"
          fontFamily="mono"
        >
          {data.progress.filled}/{data.progress.total} {t("scores")}
        </Text>
      </Flex>

      <Box
        bg="smBackground"
        rounded="xl"
        border="1px solid"
        borderColor="smBorder"
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("Criterion")}</Th>
                {data.bids.map((bid) => {
                  const isRejected = bid.status === PublicBidStatus.Rejected;
                  return (
                    <Th
                      key={bid.bidId}
                      textAlign="center"
                      bg={isRejected ? "red.50" : undefined}
                    >
                      <Stack spacing="0.5" align="center">
                        <Text>{bid.supplierName || t("Unknown")}</Text>
                        {isRejected && (
                          <Badge colorScheme="red">{t("Avvist")}</Badge>
                        )}
                        {awarded &&
                          bid.status === PublicBidStatus.Submitted && (
                            <Badge colorScheme="purple">
                              {t("Not selected")}
                            </Badge>
                          )}
                        <PriceCell
                          bid={bid}
                          readOnly={readOnly || isRejected}
                          onSave={async (value, comment) => {
                            await upsertPriceOverride({
                              eventId,
                              upsertPriceOverrideRequest: {
                                bidId: bid.bidId,
                                value,
                                comment,
                              },
                            }).unwrap();
                          }}
                        />
                      </Stack>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {data.criteria.map((criterion) => (
                <Tr key={criterion.id}>
                  <Td>
                    <Flex align="center" gap="2">
                      <Text fontWeight="medium" color="smText">
                        {criterion.name || t(criterion.type)}
                      </Text>
                      {criterion.weightPercent != null && (
                        <Badge colorScheme="teal">
                          {criterion.weightPercent}%
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  {data.bids.map((bid) => {
                    const cell = cellFor(bid, criterion);
                    const isRejected = bid.status === PublicBidStatus.Rejected;
                    return (
                      <Td
                        key={bid.bidId}
                        textAlign="center"
                        bg={isRejected ? "red.50" : undefined}
                      >
                        {cell && (
                          <ScoreCell
                            cell={cell}
                            readOnly={readOnly || isRejected}
                            onSave={async (points, comment) => {
                              await upsertScore({
                                eventId,
                                upsertScoreRequest: {
                                  criterionId: criterion.id,
                                  bidId: bid.bidId,
                                  points,
                                  comment,
                                },
                              }).unwrap();
                            }}
                          />
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
            <Tfoot>
              <Tr>
                <Td fontWeight="bold" color="smText">
                  {t("Total weighted score")}
                </Td>
                {data.bids.map((bid) => {
                  const isRejected = bid.status === PublicBidStatus.Rejected;
                  return (
                    <Td
                      key={bid.bidId}
                      textAlign="center"
                      bg={isRejected ? "red.50" : undefined}
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="lg"
                        color={isRejected ? "red.700" : "smPrimary"}
                        fontFamily="mono"
                      >
                        {bid.weightedTotal.toFixed(1)}
                      </Text>
                    </Td>
                  );
                })}
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
      </Box>

      <EvaluationRanking bids={data.bids} awarded={awarded} />

      <CriterionRationaleSection
        criteria={data.criteria}
        readOnly={readOnly}
        onSave={async (criterionId, text) => {
          await setRationale({
            eventId,
            setRationaleRequest: { criterionId, text },
          }).unwrap();
        }}
      />
    </Stack>
  );
};

const PriceScoringInfo = () => {
  const { t } = useTranslation();
  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label={t("How is the price score calculated?")}
          icon={<FiInfo />}
          size="xs"
          variant="ghost"
          color="smMuted"
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <Text fontSize="sm" color="smText">
            {t(
              "Price is scored with the proportional method: the lowest price gets the full 10 points, and every other price gets 10 × (lowest ÷ that price). Each criterion's points are then multiplied by its weight.",
            )}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const BeginEvaluationPanel = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [beginEvaluation, { isLoading }] = useBeginEvaluationMutation();

  const deadlinePassed = bidDeadlinePassed(event);

  const onBegin = async () => {
    try {
      await beginEvaluation({ eventId: event.id }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <Flex
      align={{ base: "stretch", md: "center" }}
      justify="space-between"
      direction={{ base: "column", md: "row" }}
      gap="3"
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Box>
        <Heading size="sm" mb="1">
          {t("Begin evaluation")}
        </Heading>
        <Text fontSize="sm" color="smMuted">
          {deadlinePassed
            ? t(
                "The submission deadline has passed. Open the delivered bids to start scoring.",
              )
            : t(
                "You can begin evaluation once the submission deadline has passed.",
              )}
        </Text>
      </Box>
      <Button
        colorScheme="teal"
        onClick={onBegin}
        isLoading={isLoading}
        isDisabled={!deadlinePassed}
        flexShrink={0}
      >
        {t("Begin evaluation")}
      </Button>
    </Flex>
  );
};
